import { ref, onUnmounted } from "vue";
import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import { logger } from "~/utils/logger";

interface PaymentConfirmedData {
  paymentIntentId: string;
  transactionId: number;
  amount: number;
  status: string;
  // Note: donorEmail n'est plus inclus (le frontend l'a déjà dans le formulaire)
}

export function usePaymentSocket() {
  const socket = ref<Socket | null>(null);
  const isConnected = ref(false);
  const config = useRuntimeConfig();

  /**
   * Connect to WebSocket server
   */
  const connect = () => {
    if (socket.value?.connected) {
      logger.log("✅ Socket déjà connecté");
      return socket.value;
    }

    // URL du backend (http://localhost:4000 en dev)
    const socketUrl = config.public.apiUrl || "http://localhost:4000";
    logger.log(`🔌 Connexion au serveur WebSocket: ${socketUrl}`);

    socket.value = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      withCredentials: true,
      autoConnect: true,
    });

    socket.value.on("connect", () => {
      isConnected.value = true;
      logger.log(`✅ WebSocket connecté (ID: ${socket.value?.id})`);
    });

    socket.value.on("disconnect", (reason) => {
      isConnected.value = false;
      logger.log(`❌ WebSocket déconnecté (raison: ${reason})`);
    });

    socket.value.on("connect_error", (error) => {
      logger.error("❌ Erreur de connexion WebSocket:", error);
      logger.error(`   Message: ${error.message}`);
    });

    return socket.value;
  };

  /**
   * Join a payment room to listen for notifications for a specific PaymentIntent
   */
  const joinPaymentRoom = (paymentIntentId: string) => {
    if (!socket.value) {
      logger.error("❌ Socket non initialisé");
      return;
    }

    if (!socket.value.connected) {
      logger.warn("⚠️ Socket pas encore connecté, attente de connexion...");
      // Attendre la connexion avant de joindre
      socket.value.once("connect", () => {
        socket.value!.emit("join:payment", paymentIntentId);
        logger.log(`🔔 Rejoint room payment:${paymentIntentId}`);
      });
      return;
    }

    socket.value.emit("join:payment", paymentIntentId);
    logger.log(`🔔 Rejoint room payment:${paymentIntentId}`);

    // Écouter la confirmation du serveur
    socket.value.once("joined:payment", (data: { paymentIntentId: string }) => {
      logger.log(`✅ Confirmation: rejoint room payment:${data.paymentIntentId}`);
    });
  };

  /**
   * Listen for payment:confirmed event
   */
  const onPaymentConfirmed = (
    callback: (data: PaymentConfirmedData) => void
  ) => {
    if (!socket.value) {
      logger.error("❌ Socket not initialized");
      return;
    }

    socket.value.on("payment:confirmed", (data: PaymentConfirmedData) => {
      logger.log("✅ Payment confirmed via WebSocket:", data);
      callback(data);
    });
  };

  /**
   * Listen for payment:failed event
   */
  const onPaymentFailed = (
    callback: (data: { paymentIntentId: string; error: string }) => void
  ) => {
    if (!socket.value) {
      logger.error("❌ Socket not initialized");
      return;
    }

    socket.value.on("payment:failed", (data) => {
      logger.log("❌ Payment failed via WebSocket:", data);
      callback(data);
    });
  };

  /**
   * Listen for payment:processing event
   */
  const onPaymentProcessing = (
    callback: (data: { paymentIntentId: string; status: string }) => void
  ) => {
    if (!socket.value) {
      logger.error("❌ Socket not initialized");
      return;
    }

    socket.value.on("payment:processing", (data) => {
      logger.log("⏳ Payment processing via WebSocket:", data);
      callback(data);
    });
  };

  /**
   * Disconnect and cleanup
   */
  const disconnect = () => {
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      logger.log("🔌 WebSocket disconnected");
    }
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    socket,
    isConnected,
    connect,
    joinPaymentRoom,
    onPaymentConfirmed,
    onPaymentFailed,
    onPaymentProcessing,
    disconnect,
  };
}
